import { type BookDeleteResponse, type BookListResponse, buildPath } from "@contract";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { api } from "../../../api/fetcher";
import { queryKeys } from "../../../api/keys";
import { Button } from "../../../components/control/Button";
import { ErrorBanner } from "../../../components/feedback/ErrorBanner";
import { ConfirmDialog } from "../../../components/overlay/ConfirmDialog";
import { useBookContext } from "../bookContext";

export function DeleteBook() {
  const { book } = useBookContext();
  const { t } = useTranslation("overview");
  const navigate = useNavigate();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const remove = useMutation({
    mutationFn: () =>
      api<BookDeleteResponse>(buildPath("/books/:id", { id: book.id }), { method: "DELETE" }),
    onSuccess: async () => {
      void navigate("/", { replace: true });
      await client.cancelQueries({ queryKey: queryKeys.book(book.id) });
      client.removeQueries({ queryKey: queryKeys.book(book.id) });
      client.setQueryData<BookListResponse>(queryKeys.bookList, (previous) =>
        previous ? { books: previous.books.filter((entry) => entry.id !== book.id) } : previous,
      );
      await client.invalidateQueries({ queryKey: queryKeys.bookList });
    },
  });
  return (
    <div className="border-t border-line-soft pt-4">
      <Button
        size="sm"
        variant="quiet"
        onClick={() => {
          remove.reset();
          setOpen(true);
        }}
      >
        {t("delete.action")}
      </Button>
      <ConfirmDialog
        open={open}
        title={t("delete.title")}
        confirmLabel={t("delete.action")}
        tone="danger"
        busy={remove.isPending}
        onCancel={() => setOpen(false)}
        onConfirm={() => remove.mutate()}
      >
        <div className="flex flex-col gap-3">
          <p className="break-words">
            {t("delete.body", { title: book.title ?? t("book.untitled") })}
          </p>
          <ErrorBanner error={remove.error} />
        </div>
      </ConfirmDialog>
    </div>
  );
}
